








































































import moment from "moment";
import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { BFormCheckbox } from "bootstrap-vue";

import { PatientDetailsViewModel, Infusion, ClientSettings } from "@/models";
import * as arrays from "@/utils/array";
import { formatVentNumber } from "@/filters/ventFields";
import _auth from "@/utils/auth";
import InfusionChangeDirection from "./InfusionChangeDirection.vue";
import InfusionGuardrailStatus from "./InfusionGuardrailStatus.vue";

export interface InfusionChange {
  /** This is need by v-for as a unique identifier for the object */
  key: string;
  date: string;
  drug: string;
  displayOrder: number;
  from: string;
  to: string;
  isBolus: boolean;
  direction: number;
}

export interface GroupedInfusions {
  /** This is needed by v-for as a unique identifier for the object */
  key: string;
  date: moment.Moment;
  changes: InfusionChange[];
}

export const ShowOnlyBolusesStorageKey = "Messenger_Infusion_ShowOnlyBoluses";

@Component({
  components: { InfusionChangeDirection, InfusionGuardrailStatus, BFormCheckbox }
})
export default class PatientInfusion extends Vue {
  // Use a class property for dependencies so unit tests can override them
  auth = _auth;

  @Prop() readonly patient!: PatientDetailsViewModel;

  private config: ClientSettings | null = null;

  // Use local storage to save the user's selection across the different patients
  private showOnlyBoluses = false;

  async created() {
    this.loadPreferences();

    // The clients settings won't change while on this page
    this.config = await this.auth.getClientSettings();
  }

  loadPreferences() {
    this.showOnlyBoluses = this.$localStorage.getItem(ShowOnlyBolusesStorageKey) === "true";
  }

  savePreferences() {
    this.$localStorage.setItem(ShowOnlyBolusesStorageKey, this.showOnlyBoluses ? "true" : "");
  }

  get hoursOfInfusionHistory(): number | undefined {
    return this.config?.infusionHistoryInHours;
  }

  get currentInfusions(): Infusion[] {
    if (!this.patient?.infusionHistory?.length) return [];

    // Find any infusions that are still active and then put them in the display order as defined in RKP
    return arrays.sort(
      this.patient.infusionHistory.filter(i => !i.timeEndedLocal),
      { field: "drugDisplayOrder" },
      { field: "drugName" }
    );
  }

  get unfilteredHistory(): InfusionChange[] {
    // Create a new array so that the patient's infusionHistory property isn't changed
    const infusions = ([] as Infusion[]).concat(this.patient?.infusionHistory || []);
    if (!infusions?.length) return [];

    return this.trackInfusionChangesByDrug(infusions);
  }

  get filteredHistory(): GroupedInfusions[] {
    let history = this.unfilteredHistory;
    if (this.showOnlyBoluses) history = history.filter(h => h.isBolus);

    return this.groupHistoryByDate(history);
  }

  trackInfusionChangesByDrug(infusions: Infusion[]): InfusionChange[] {
    // Wait until the configuration is loaded before trying to calculate
    if (!this.config) return [];

    // If there is infusion data, we can assume client's last infusion load time is populated
    const lastInfusionBatchTime = moment.utc(this.patient.timeLastClientInfusionLoadUtc as string);
    const cutoff = lastInfusionBatchTime.subtract(this.hoursOfInfusionHistory, "hours");

    const previousInfusionsByDrug: { [drug: string]: Infusion } = {};
    const history: InfusionChange[] = [];

    // Ensure the infusions are sorted oldest to newest before processing them
    // Use UTC so that sorting still works across daylight savings
    arrays.sort(infusions, { field: "timeStartedUtc" }, { field: "drugName" });

    infusions.forEach(infusion => {
      const drug = infusion.drugName;
      const startUtc = moment.utc(infusion.timeStartedUtc);

      // If the infusion started before the cutoff, don't include it in the history, but do add it to the map tracking
      // the previous infusion so that if there is a chance, we can show what the previous value was
      if (startUtc.isBefore(cutoff)) {
        // If for some reason, the infusion ended before the cutoff, don't show it
        if (!infusion.timeEndedUtc || moment.utc(infusion.timeEndedUtc).isSameOrAfter(cutoff)) {
          previousInfusionsByDrug[drug] = infusion;
        }

        return;
      }

      // Find any changes between the last infusion (for the same drug) and the current infusion (if any)
      const previous = previousInfusionsByDrug[drug];
      history.push(...this.trackChanges(previous, infusion));

      // Update the current infusion to be the previous infusion (for the next iteration)
      previousInfusionsByDrug[drug] = infusion;
    });

    // Check if any drugs were ended without a new infusion taking place. If so, create a stop message
    Object.values(previousInfusionsByDrug).forEach(previousInfusion => {
      if (previousInfusion.timeEndedLocal) {
        history.push(this.createStopMessage(previousInfusion));
      }
    });

    // The history needs to be sorted to ensure it is in date descending order
    return arrays.sort(history, { field: "date", isDescending: true }, { field: "displayOrder" });
  }

  trackChanges(previous: Infusion | undefined, current: Infusion): InfusionChange[] {
    if (!previous) {
      return [this.createStartMessage(current)];
    }

    // If there is a gap between the infusions, treat them as separate stops and starts
    // Use UTC to make sure daylight saving time is handled correctly
    // FYI moment treats a difference of 59.999 seconds as 0 minutes
    const gap = moment.utc(current.timeStartedUtc).diff(moment.utc(previous.timeEndedUtc), "minutes");
    if (gap > 0) {
      return [this.createStopMessage(previous), this.createStartMessage(current)];
    }

    if (this.hasDifferences(previous, current)) {
      return [this.createChangeMessage(previous, current)];
    }

    // If there aren't any differences, don't return an infusion change
    return [];
  }

  createStartMessage(to: Infusion) {
    return this.createChangeMessage(undefined, to);
  }

  createStopMessage(from: Infusion) {
    return this.createChangeMessage(from, undefined);
  }

  createChangeMessage(from?: Infusion, to?: Infusion): InfusionChange {
    function toString(i?: Infusion) {
      if (!i) return "Stopped";

      // Use formatVentNumber to round to 2 decimals
      let val = `${formatVentNumber(i.doseAmount)} ${i.doseUnits}`;
      if (i.isBolus) val += " (Bolus)";

      return val;
    }

    // If there's a from but no to, this is a stop
    const isStop = !!from && !to;

    return {
      // v-for needs a unique key and since an infusion can be listed twice (1x for start and 1x for stop), include the
      // event type with the ID to make it unique
      key: isStop ? `${from?.id}_Stop` : `${to?.id}_Start`,
      date: (isStop ? from?.timeEndedLocal : to?.timeStartedLocal) as string,
      // These will be the same, so just take whichever is available
      drug: from?.drugName || (to?.drugName as string),
      displayOrder: from?.drugDisplayOrder || (to?.drugDisplayOrder as number),
      from: toString(from),
      to: toString(to),
      isBolus: !!to?.isBolus,
      direction: this.calculateDirection(from, to)
    };
  }

  hasDifferences(a: Infusion, b: Infusion): boolean {
    if (a.isBolus !== b.isBolus) return true;
    if (a.doseUnits !== b.doseUnits) return true;

    // Round before comparing the amounts, that way if there isn't a visible change it isn't shown to the user
    // E.g. from 1.559 to 1.555 would show in the UI from 1.55 to 1.55. It's better to just not show this and treat
    // them as equal.
    if (formatVentNumber(a.doseAmount) !== formatVentNumber(b.doseAmount)) return true;

    return false;
  }

  calculateDirection(from?: Infusion, to?: Infusion): number {
    const stopped = 0;
    const decreased = -1;
    const increased = 1;

    if (!to) return stopped;
    // Starting infusion is treated as an increase
    if (!from) return increased;

    // A bolus is a short dose of increased sedation, so treat starting a bolus as an increase and ending as a decrease
    if (to.isBolus && !from.isBolus) return increased;
    if (from.isBolus && !to.isBolus) return decreased;

    // Assume that the units are the same (either comparing bolus to bolus or continuous to continuous)
    // In the unlikely event that the amounts are the same (it shouldn't make it hear because there wouldn't be change),
    // treat it as an increase (this should only be applicable to unit tests where checking change to only the units)
    if (to.doseAmount < from.doseAmount) return decreased;
    else return increased;
  }

  groupHistoryByDate(boluses: InfusionChange[]): GroupedInfusions[] {
    const results: GroupedInfusions[] = [];

    boluses.forEach(i => {
      const last = results[results.length - 1];
      if (last?.date.isSame(i.date, "day")) {
        last.changes.push(i);
      } else {
        const date = moment(i.date).startOf("day");
        results.push({
          key: date.format("L"),
          date: date,
          changes: [i]
        });
      }
    });

    return results;
  }
}
